import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api, { authService } from '../services/api';

interface User {
  id: string;
  auth_id: string;
  email: string;
  username: string;
  full_name: string;
  email_verified: boolean;
  is_admin: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username?: string, full_name?: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  loginWithOAuth: (provider: string) => void;
  completeOAuth: (loginToken: string) => Promise<void>;
  logout: (allSessions?: boolean) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('refresh_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    // Check for OAuth completion parameters
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    const loginToken = urlParams.get('login_token');
    const oauthError = urlParams.get('oauth_error');
    const provider = urlParams.get('provider');

    if (oauthSuccess === 'true' && loginToken) {
      // Complete OAuth flow
      completeOAuth(loginToken).catch(error => {
        console.error('OAuth completion failed:', error);
      });
      
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('oauth_success');
      newUrl.searchParams.delete('login_token');
      newUrl.searchParams.delete('provider');
      window.history.replaceState({}, '', newUrl.toString());
      
      return;
    }

    if (oauthError && provider) {
      // Handle OAuth error
      const errorMessages: Record<string, string> = {
        'access_denied': 'Access was denied. Please try again.',
        'invalid_request': 'Invalid OAuth request. Please try again.',
        'invalid_state': 'Invalid OAuth state. Please try again.',
        'missing_parameters': 'Missing OAuth parameters. Please try again.',
        'account_disabled': 'Your account has been disabled. Please contact support.',
        'callback_failed': 'OAuth authentication failed. Please try again.'
      };
      
      const errorMessage = errorMessages[oauthError] || `${provider} authentication failed. Please try again.`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
      
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('oauth_error');
      newUrl.searchParams.delete('provider');
      window.history.replaceState({}, '', newUrl.toString());
      
      setLoading(false);
      return;
    }

    // Normal authentication flow
    if (token) {
      fetchUser();
    } else if (refreshToken) {
      // Try to refresh the token
      attemptTokenRefresh();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await authService.me();
      setUser(response.data);
    } catch (error: any) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401 && refreshToken) {
        // Try to refresh token
        await attemptTokenRefresh();
      } else {
        clearTokens();
      }
    } finally {
      setLoading(false);
    }
  };

  const attemptTokenRefresh = async () => {
    if (!refreshToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await authService.refreshToken(refreshToken);
      const { access_token } = response.data;
      
      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await fetchUser();
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearTokens();
    }
  };

  const clearTokens = () => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
  };

  const setTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    setToken(accessToken);
    setRefreshToken(refreshToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      const { user: userData, access_token, refresh_token } = response.data;
      
      setTokens(access_token, refresh_token);
      setUser(userData);
      
      enqueueSnackbar('Login successful!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const register = async (email: string, password: string, username?: string, full_name?: string) => {
    try {
      const response = await authService.register(email, password, username, full_name);
      const { user: userData, access_token, refresh_token } = response.data;
      
      setTokens(access_token, refresh_token);
      setUser(userData);
      
      enqueueSnackbar('Account created successfully!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const requestMagicLink = async (email: string) => {
    try {
      await authService.requestMagicLink(email);
      enqueueSnackbar('Magic link sent to your email!', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to send magic link';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const verifyMagicLink = async (token: string) => {
    try {
      const response = await authService.verifyMagicLink(token);
      const { access_token, refresh_token } = response.data;

      // Set tokens first
      setTokens(access_token, refresh_token);

      // Fetch complete user data from /me endpoint
      const userResponse = await authService.me();
      setUser(userResponse.data);

      enqueueSnackbar('Logged in successfully!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Invalid or expired magic link';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      await authService.requestPasswordReset(email);
      enqueueSnackbar('Password reset link sent to your email!', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to send reset link';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      await authService.resetPassword(token, password);
      enqueueSnackbar('Password reset successfully! Please log in.', { variant: 'success' });
      navigate('/login');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Password reset failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const loginWithOAuth = (provider: string) => {
    // Store the provider for potential error handling
    sessionStorage.setItem('oauth_provider', provider);
    authService.initiateOAuth(provider);
  };

  const completeOAuth = async (loginToken: string) => {
    try {
      const response = await authService.completeOAuth(loginToken);
      const { user: userData, access_token, refresh_token } = response.data;
      
      setTokens(access_token, refresh_token);
      setUser(userData);
      
      // Clear OAuth provider from session
      sessionStorage.removeItem('oauth_provider');
      sessionStorage.removeItem('oauth_error');
      
      enqueueSnackbar('Logged in successfully!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'OAuth login failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      
      // Store error for display on login page
      sessionStorage.setItem('oauth_error', errorMessage);
      navigate('/login');
      throw error;
    }
  };

  const logout = async (allSessions: boolean = false) => {
    try {
      await authService.logout(refreshToken || undefined, allSessions);
      clearTokens();
      enqueueSnackbar(
        allSessions ? 'Logged out from all devices' : 'Logged out successfully', 
        { variant: 'info' }
      );
      navigate('/login');
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      clearTokens();
      enqueueSnackbar('Logged out successfully', { variant: 'info' });
      navigate('/login');
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    requestMagicLink,
    verifyMagicLink,
    requestPasswordReset,
    resetPassword,
    loginWithOAuth,
    completeOAuth,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};